import React, { useId } from 'react';
import CallRoundedIcon from '@mui/icons-material/CallRounded';

import { formatPhoneE164ToDisplay } from 'components/CallScribe/Recents/recentsHelpers';
import usePopupCallViewStyles from 'components/CallScribe/popupCallViewStyles';

export interface IBetweenCallsFrameProps {
  target: string;
  otherNumber: string;
  onOtherNumberChange: (value: string) => void;
  busy: boolean;
  canCallAgain: boolean;
  onCallAgain: () => void;
  onCallOther: () => void;
}

/**
 * The call window between calls (MSPPROC-19105).
 *
 * This screen used to be a dead end: "Call already placed — This window was refreshed during a
 * call, so the call has ended. Close it and start a new call from the ticket." Masood reached it
 * wanting to ring a customer back who had not answered, and was told to close the window.
 *
 * The copy was also wrong about what had happened — it described our session guard rather than the
 * user's situation, and he had not refreshed mid-call.
 *
 * The guard itself is unchanged and stays: a refresh must never silently redial a customer. What
 * this adds is dialling again ON PURPOSE. Nothing here dials on render; both actions are clicks.
 */
const BetweenCallsFrame: React.FC<IBetweenCallsFrameProps> = ({
  target,
  otherNumber,
  onOtherNumberChange,
  busy,
  canCallAgain,
  onCallAgain,
  onCallOther,
}) => {
  const classes = usePopupCallViewStyles();
  const fieldId = useId();
  const shown = formatPhoneE164ToDisplay(target) || target;

  return (
    <div className={classes.betweenCalls}>
      <div className={classes.betweenCallsHead}>
        <h2 className={classes.betweenCallsTitle}>This call has finished</h2>
        <p className={classes.betweenCallsHint}>
          Call the same person back, or dial a different number, without leaving this window.
        </p>
      </div>

      <button
        type="button"
        className={classes.betweenCallsAgain}
        disabled={!canCallAgain || busy}
        onClick={onCallAgain}
      >
        <CallRoundedIcon aria-hidden />
        {busy ? 'Calling…' : `Call ${shown} again`}
      </button>

      <div className={classes.betweenCallsDivider} aria-hidden>or</div>

      <div className={classes.betweenCallsField}>
        <label htmlFor={fieldId} className={classes.betweenCallsLabel}>Call a different number</label>
        <input
          id={fieldId}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          className={classes.betweenCallsInput}
          value={otherNumber}
          disabled={busy}
          onChange={e => { onOtherNumberChange(e.target.value); }}
        />
      </div>
      <button
        type="button"
        className={classes.betweenCallsOther}
        disabled={busy || otherNumber.trim().length === 0}
        onClick={onCallOther}
      >
        Call this number
      </button>
    </div>
  );
};

export default BetweenCallsFrame;
